/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: MPL-2.0
 */

import type { ComponentProps, ReactNode } from 'react'
import { HDSPlusButton } from '@hashicorp/react-mds/src/hds-plus/button'
import ButtonSet from '@hashicorp/react-hds/src/components/button-set'
import { HDSPlusStandaloneLink } from '@hashicorp/react-mds/src/hds-plus/standalone-link'
import { IconArrowRight24 } from '@hashicorp/flight-icons/svg-react/arrow-right-24'
import { IconExternalLink24 } from '@hashicorp/flight-icons/svg-react/external-link-24'
import { IconDownload24 } from '@hashicorp/flight-icons/svg-react/download-24'
import { IconCornerRightDown24 } from '@hashicorp/flight-icons/svg-react/corner-right-down-24'
import type { Products } from '@hashicorp/platform-product-meta'
import classNames from 'classnames'
import variantCentered from './styles/variant-centered.module.css'
import variantCompact from './styles/variant-compact.module.css'
import variantCompactGrid from './styles/variant-compact-grid.module.css'
import variantLinks from './styles/variant-links.module.css'

const stylesDict = {
	centered: variantCentered,
	compact: variantCompact,
	compactGrid: variantCompactGrid,
	links: variantLinks,
}

interface CallToActionProps {
	heading?: string
	content?: ReactNode
	links?: {
		type?: 'inbound' | 'outbound' | 'anchor' | 'download'
		text: string
		url: string
	}[]
	variant?: 'centered' | 'compact' | 'compactGrid' | 'links'
	product?: 'primary' | 'secondary' | Exclude<Products, 'hashicorp'>
	theme?: 'light' | 'gray' | 'dark' | 'brand'
	className?: string
}

function determineIconFromLinktype(
	linkType: 'inbound' | 'outbound' | 'anchor' | 'download'
): ReactNode {
	switch (linkType) {
		case 'inbound':
			return (
				<IconArrowRight24
					style={{ display: 'block' }}
					width="100%"
					height="100%"
				/>
			)
		case 'outbound':
			return (
				<IconExternalLink24
					style={{ display: 'block' }}
					width="100%"
					height="100%"
				/>
			)
		case 'download':
			return (
				<IconDownload24
					style={{ display: 'block' }}
					width="100%"
					height="100%"
				/>
			)
		case 'anchor':
			return (
				<IconCornerRightDown24
					style={{ display: 'block' }}
					width="100%"
					height="100%"
				/>
			)
		default:
			throw new Error(`Unsupported linkType: ${linkType}`)
	}
}

function CallToAction({
	heading,
	content,
	links,
	variant = 'centered',
	product = 'primary',
	theme = 'light',
	className,
}: CallToActionProps) {
	const s = stylesDict[variant]
	if (!heading && !content) {
		throw new Error('<CallToAction /> requires either heading or content')
	}
	const hasLinks = links && links.length > 0
	if (hasLinks && links.filter((l) => !l.text || !l.url).length > 0) {
		throw new Error(
			'<CallToAction /> `links` must have both a "text" and a "url" prop'
		)
	}
	return (
		<div className={classNames(s.root, s[`theme-${theme}`], className)}>
			<div className={s.container}>
				{heading && (
					<h2 className={s.heading} data-testid="heading">
						{heading}
					</h2>
				)}
				<div className={s.contentAndLinks}>
					{content && (
						<div
							className={classNames(s.content, { [s.hasHeading]: heading })}
							data-testid="content"
						>
							{content}
						</div>
					)}
					{links && (
						<ButtonSet className={s.links} data-testid="links">
							{links.map((link, stableIdx) => {
								let color: ComponentProps<typeof HDSPlusButton>['color'] =
									variant === 'links'
										? 'tertiary'
										: stableIdx === 0
										? product
										: 'secondary'

								if (color === 'tertiary') {
									return (
										<HDSPlusStandaloneLink
											key={stableIdx}
											color="secondary"
											text={link.text}
											href={link.url}
											size="large"
											icon={
												<IconArrowRight24
													style={{ display: 'block' }}
													width="100%"
													height="100%"
												/>
											}
											iconPosition="trailing"
										/>
									)
								}

								// @ts-expect-error - TypeScript doesn't allow 'hashicorp', but
								// Dato might still pass it, so rewrite it to 'primary'.
								if (color === 'hashicorp') {
									color = 'primary'
								}

								if (theme === 'brand' && color === 'primary') {
									// `primary` doesn't work on brand, so
									// update it to `secondary`
									color = 'secondary'
								} else if (theme === 'brand' && color === 'secondary') {
									return (
										<HDSPlusStandaloneLink
											key={stableIdx}
											color="secondary-inverted"
											text={link.text}
											href={link.url}
											size="large"
											icon={
												<IconArrowRight24
													style={{ display: 'block' }}
													width="100%"
													height="100%"
												/>
											}
											iconPosition="trailing"
										/>
									)
								}

								return (
									<HDSPlusButton
										// eslint-disable-next-line react/no-array-index-key
										key={stableIdx}
										// className={s.button}
										color={color}
										text={link.text}
										href={link.url}
										size="large"
										icon={
											link.type
												? determineIconFromLinktype(link.type)
												: undefined
										}
										iconPosition="trailing"
									/>
								)
							})}
						</ButtonSet>
					)}
				</div>
			</div>
		</div>
	)
}

export default CallToAction
